import React, { useState } from "react";
import { Minus, Plus, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getFileUrl } from "@/lib/utils";
import type { CartItem } from "@/features/cart/store/cart-store";
import { useCheckoutCart } from "../hooks/use-checkout-cart";

interface CheckoutCartItemProps {
  item: CartItem;
  formatPrice: (price: number) => string;
}

export const CheckoutCartItem: React.FC<CheckoutCartItemProps> = ({
  item,
  formatPrice,
}) => {
  const { t } = useTranslation();
  const { updateQuantity, removeFromCart } = useCheckoutCart();
  const [isBusy, setIsBusy] = useState(false);

  const itemPrice =
    typeof item.price === "number" && !isNaN(item.price) ? item.price : 0;
  const itemTotal = itemPrice * item.quantity;

  const variant1 =
    item.metadata?.axis1Name && item.metadata?.a1Value
      ? item.metadata.a1Value
      : null;
  const variant2 =
    item.metadata?.axis2Name && item.metadata?.a2Value
      ? item.metadata.a2Value
      : null;
  const variantText = [variant1, variant2].filter(Boolean).join(" / ");

  const imageUrl = item.file
    ? typeof item.file === "string" &&
      (item.file.startsWith("http://") || item.file.startsWith("https://"))
      ? item.file
      : getFileUrl(item.file) || item.file
    : null;

  const stockCount =
    typeof item.metadata?.stockCount === "number"
      ? item.metadata.stockCount
      : undefined;
  const isAtMaxStock =
    typeof stockCount === "number" && item.quantity >= stockCount;

  const runAction = async (action: () => Promise<void>) => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      await action();
    } finally {
      setIsBusy(false);
    }
  };

  const handleIncrease = () => {
    if (isAtMaxStock) return;
    void runAction(() => updateQuantity(item._id, item.quantity + 1));
  };

  const handleDecrease = () => {
    if (item.quantity > 1) {
      void runAction(() => updateQuantity(item._id, item.quantity - 1));
      return;
    }
    void runAction(() => removeFromCart(item._id));
  };

  const handleRemove = () => {
    void runAction(() => removeFromCart(item._id));
  };

  return (
    <div className="flex gap-3 md:gap-4">
      {imageUrl ? (
        <img
          src={imageUrl}
          alt={item.title || ""}
          className="w-14 h-14 md:w-16 md:h-16 object-cover rounded-lg bg-gray-100 dark:bg-gray-800"
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      ) : (
        <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
          <span className="text-[10px] md:text-xs text-gray-400">
            {t("checkout.noImage")}
          </span>
        </div>
      )}

      <div className="flex-1 flex flex-col gap-1.5 min-w-0">
        <div className="flex justify-between items-start gap-2">
          <p className="text-xs md:text-sm lg:text-[14px] font-semibold text-[#121926] dark:text-gray-100 line-clamp-2">
            {item.title}
          </p>
          <p className="text-xs md:text-sm lg:text-[14px] font-semibold text-[#121926] dark:text-gray-100 whitespace-nowrap">
            {formatPrice(itemTotal)}
          </p>
        </div>

        {variantText && (
          <p className="text-[10px] md:text-xs lg:text-[12px] font-medium text-[#364152] dark:text-gray-400">
            {variantText}
          </p>
        )}

        <div className="flex items-center justify-between gap-2 pt-0.5">
          <div className="inline-flex items-center rounded-lg border border-border-secondary dark:border-gray-600 overflow-hidden">
            <button
              type="button"
              onClick={handleDecrease}
              disabled={isBusy}
              aria-label={t("checkout.decreaseQuantity")}
              className="w-7 h-7 flex items-center justify-center text-text-secondary dark:text-gray-300 hover:bg-bg-secondary dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              <Minus className="w-3 h-3" />
            </button>
            <span className="min-w-[1.75rem] text-center text-xs md:text-sm font-semibold text-text-primary dark:text-gray-100">
              {item.quantity}
            </span>
            <button
              type="button"
              onClick={handleIncrease}
              disabled={isBusy || isAtMaxStock}
              aria-label={t("checkout.increaseQuantity")}
              className="w-7 h-7 flex items-center justify-center text-text-secondary dark:text-gray-300 hover:bg-bg-secondary dark:hover:bg-gray-700 disabled:opacity-50 transition-colors"
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>

          <button
            type="button"
            onClick={handleRemove}
            disabled={isBusy}
            aria-label={t("cart.remove")}
            className="w-7 h-7 flex items-center justify-center rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
};
